// Guest voices (protocol 1.8, feature `guests`): with "only listen to me" on, the kit groups the
// speech it ignores by voice into the session's guests ("Guest 1", "Guest 2", or a name the user
// gave). This model keeps a transcript per guest for a sidebar. It is an interruption log, not
// the conversation: none of it was sent to the assistant.
//
// Session guests (and their voices, in the kit) end with the session. Their lines stay on screen,
// marked as from the last session, until the next session starts. A guest the user named and
// chose to remember is `saved`: the kit recognises them next time.

import type { GuestInfo, Id, ServerMsg, SpeakerProfileInfo } from "./protocol";

/** One thing a guest said. */
export interface GuestLine {
  /** When the page got it (`Date.now()`). */
  at: number;
  /** Absent when the host keeps other people's words in the kit (only that they spoke). */
  text?: string;
  voicedMs: number;
  /** Said inside this turn of the user's (an utterance with both voices). */
  turnId?: Id;
  /** 1.10: its id, for "That was me" (`speech.mine`): it was the user, not this guest. */
  ignoredId?: string;
}

export interface Guest {
  /** The kit's id (`g<n>`), or `""` for speech too short to tell whose ("Someone"). */
  id: string;
  label: string;
  name?: string;
  /** Their voice is remembered (recognised next session). */
  saved: boolean;
  /** Asked to be remembered; the kit saves it once it has heard enough of them. */
  savePending: boolean;
  speechMs: number;
  lines: GuestLine[];
  /** Their session ended (or the kit forgot them): no longer renameable, kept for reading. */
  ended: boolean;
  /** Lines since the panel was last looked at. */
  unread: number;
}

/** The pseudo-guest for speech too short to tell whose. */
export const SOMEONE_ID = "";

export interface GuestModelOptions {
  /** Most lines kept per guest. Default 200. */
  maxLines?: number;
}

export class GuestModel {
  /** In the order they were first heard. */
  guests: Guest[] = [];
  /** Saved guests (names and sizes only), for settings. */
  saved: SpeakerProfileInfo[] = [];
  /** The kit groups voices into guests (it said so in `welcome`, or sent a guest event). */
  active = false;
  private readonly maxLines: number;
  private readonly listeners: Array<() => void> = [];

  constructor(opts: GuestModelOptions = {}) {
    this.maxLines = opts.maxLines ?? 200;
  }

  onChange(fn: () => void): () => void {
    this.listeners.push(fn);
    return () => {
      const i = this.listeners.indexOf(fn);
      if (i >= 0) this.listeners.splice(i, 1);
    };
  }

  /** Feed every kit event here. */
  apply(m: ServerMsg): void {
    switch (m.type) {
      case "welcome":
        if (m.features?.includes("guests") && !this.active) {
          this.active = true;
          this.changed();
        }
        break;
      case "guest.speech":
        this.active = true;
        this.heard(m.guest_id ?? SOMEONE_ID, m.label, m.text, m.voiced_ms, m.turn_id, Date.now(), m.ignored_id);
        break;
      case "speech.reattributed":
        this.reattributed(m.ignored_id, m.guest_removed ? m.guest_id : undefined);
        break;
      case "guest.list":
        this.active = true;
        this.list(m.guests, m.saved);
        break;
      case "session.started":
        this.newSession();
        break;
    }
  }

  find(id: string): Guest | undefined {
    return this.guests.find((g) => g.id === id);
  }

  /** The label for a guest id (as the kit last named them), or "Someone". */
  label(id: string | undefined): string {
    return (id ? this.find(id)?.label : undefined) ?? "Someone";
  }

  /** Guests with something to show (any line, or still in the session). */
  get visible(): Guest[] {
    return this.guests.filter((g) => g.lines.length || !g.ended);
  }

  get unread(): number {
    return this.guests.reduce((n, g) => n + g.unread, 0);
  }

  /** The panel is being looked at: nothing is unread. */
  markRead(): void {
    if (!this.unread) return;
    for (const g of this.guests) g.unread = 0;
    this.changed();
  }

  /** The user forgot this guest: off the list. */
  remove(id: string): void {
    const n = this.guests.length;
    this.guests = this.guests.filter((g) => g.id !== id);
    if (this.guests.length !== n) this.changed();
  }

  clear(): void {
    this.guests = [];
    this.changed();
  }

  /**
   * 1.10, "That was me": the speech `ignoredId` was the user's. Its lines leave the guest; a
   * session guest that said nothing else (`removedGuest`, the kit dropped them) goes too.
   */
  reattributed(ignoredId: string, removedGuest?: string): void {
    let any = false;
    for (const g of this.guests) {
      const n = g.lines.length;
      g.lines = g.lines.filter((l) => l.ignoredId !== ignoredId);
      if (g.lines.length !== n) {
        any = true;
        g.unread = Math.min(g.unread, g.lines.length);
      }
    }
    const before = this.guests.length;
    if (removedGuest) this.guests = this.guests.filter((g) => g.id !== removedGuest);
    this.guests = this.guests.filter((g) => g.id !== SOMEONE_ID || g.lines.length > 0);
    if (any || this.guests.length !== before) this.changed();
  }

  private heard(id: string, label: string, text: string | undefined, voicedMs: number, turnId?: Id, at = Date.now(), ignoredId?: string): void {
    let g = this.find(id);
    if (!g) {
      g = { id, label, saved: false, savePending: false, speechMs: 0, lines: [], ended: false, unread: 0 };
      this.guests.push(g);
    }
    g.label = label;
    g.ended = false;
    if (id === SOMEONE_ID) g.speechMs += voicedMs;
    const line: GuestLine = { at, voicedMs };
    if (text) line.text = text;
    if (turnId != null) line.turnId = turnId;
    if (ignoredId) line.ignoredId = ignoredId;
    g.lines.push(line);
    if (g.lines.length > this.maxLines) g.lines.splice(0, g.lines.length - this.maxLines);
    g.unread++;
    this.changed();
  }

  private list(guests: GuestInfo[], saved: SpeakerProfileInfo[]): void {
    this.saved = saved;
    const live = new Set(guests.map((g) => g.guest_id));
    for (const info of guests) {
      let g = this.find(info.guest_id);
      if (!g) {
        g = { id: info.guest_id, label: info.label, saved: false, savePending: false, speechMs: 0, lines: [], ended: false, unread: 0 };
        this.guests.push(g);
      }
      g.label = info.label;
      g.name = info.name;
      g.saved = !!info.saved;
      g.savePending = !!info.save_pending;
      g.speechMs = info.speech_ms;
      g.ended = false;
    }
    for (const g of this.guests) if (g.id !== SOMEONE_ID && !live.has(g.id)) g.ended = true;
    this.changed();
  }

  /** A new session: the last session's guests (ended) go; this one's come as they are heard. */
  private newSession(): void {
    const n = this.guests.length;
    this.guests = this.guests.filter((g) => !g.ended && g.id !== SOMEONE_ID);
    if (this.guests.length !== n) this.changed();
  }

  private changed(): void {
    for (const fn of this.listeners) fn();
  }
}

/** The one-line consent note shown before a guest's voice is saved. */
export function consentNote(name: string): string {
  const who = name.trim() || "their";
  return `Save ${who === "their" ? "their" : `${who}'s`} voice so I recognise them next time? Only do this with their OK.`;
}
